import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Check, X } from "lucide-react";
import { ProductTable, ColourTable, CustomerPriceTable } from "@/components/products/ProductTables";
import { toast } from "sonner";

const TABS = [["products", "Product Master"], ["colours", "Colour Master"], ["pricing", "Customer Pricing"], ["approvals", "Price Approvals"]];
const STATUS = ["active", "inactive", "discontinued"];
const FLAG_LABEL = { below_cost: "Below Cost", below_floor: "Below Floor Price", below_msp: "Below MSP", over_discount: "Over Max Discount" };
const FLAG_CLS = { below_cost: "bg-signal-red/15 text-signal-red", below_floor: "bg-signal-red/15 text-signal-red", below_msp: "bg-signal-amber/15 text-signal-amber", over_discount: "bg-signal-amber/15 text-signal-amber" };

const EMPTY_PRODUCT = {
  sku: "", name: "", category: "", subcategory: "", product_type: "", brand: "", customer_ref: "", status: "active",
  hsn: "", uom: "PCS", standard_rate: "", msp: "", floor_price: "", max_discount_pct: "", cost_price: "",
  gst_percent: 18, barcode: "", description: "", default_warehouse: "", packing_type: "", pieces_per_carton: "",
  min_stock: "", max_stock: "", reorder_level: "", standard_location: "", weight: "", dimensions: "", process_route: [], price_change_reason: "",
  specs: { capacity: "", capacity_unit: "ml", material: "", bottle_weight_g: "", neck_size_mm: "", neck_type: "", shape: "", colour: "", diameter_mm: "", height_mm: "", custom_specs: "" },
  packaging: { carton_type: "", carton_code: "", carton_weight_g: "", carton_length_mm: "", carton_width_mm: "", carton_height_mm: "" },
};
const EMPTY_COLOUR = { name: "", code: "", masterbatch_ref: "", customer_ref: "", remarks: "" };
const EMPTY_CP = { customer_code: "", sku: "", price: "", effective_from: "", effective_to: "", remarks: "" };

const fnum = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));
const fint = (v) => (v === "" || v === null || v === undefined ? null : parseInt(v));
const str = (v) => (v === "" ? null : v);
const money = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

function cleanProduct(f) {
  const specs = Object.fromEntries(Object.entries(f.specs).map(([k, v]) => [k, ["bottle_weight_g", "neck_size_mm", "diameter_mm", "height_mm"].includes(k) ? fnum(v) : str(v)]));
  const packaging = Object.fromEntries(Object.entries(f.packaging).map(([k, v]) => [k, ["carton_weight_g", "carton_length_mm", "carton_width_mm", "carton_height_mm"].includes(k) ? fnum(v) : str(v)]));
  return {
    sku: f.sku, name: f.name, category: str(f.category), subcategory: str(f.subcategory), product_type: str(f.product_type),
    brand: str(f.brand), customer_ref: str(f.customer_ref), status: f.status, hsn: str(f.hsn), uom: f.uom || "PCS",
    standard_rate: fnum(f.standard_rate) ?? 0, msp: fnum(f.msp), floor_price: fnum(f.floor_price),
    max_discount_pct: fnum(f.max_discount_pct), cost_price: fnum(f.cost_price), gst_percent: fnum(f.gst_percent) ?? 18,
    barcode: str(f.barcode), description: str(f.description), default_warehouse: str(f.default_warehouse),
    packing_type: str(f.packing_type), pieces_per_carton: fint(f.pieces_per_carton),
    min_stock: fint(f.min_stock), max_stock: fint(f.max_stock), reorder_level: fint(f.reorder_level),
    standard_location: str(f.standard_location), weight: fnum(f.weight), dimensions: str(f.dimensions),
    process_route: Array.isArray(f.process_route) && f.process_route.length ? f.process_route : null,
    price_change_reason: str(f.price_change_reason),
    specs, packaging,
  };
}

function F({ label, children, testId }) {
  return <div><Label className="text-xs">{label}</Label>{children}</div>;
}

export default function Products() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [colours, setColours] = useState([]);
  const [custPrices, setCustPrices] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSku, setEditSku] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [colourForm, setColourForm] = useState(EMPTY_COLOUR);
  const [cpForm, setCpForm] = useState(EMPTY_CP);
  const [cpOpen, setCpOpen] = useState(false);

  const canEdit = ["admin", "warehouse_manager"].includes(me?.role);
  const isAdmin = me?.role === "admin";

  const load = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/colours").then((r) => setColours(r.data)).catch(() => {});
    api.get("/customer-prices").then((r) => setCustPrices(r.data)).catch(() => {});
    api.get("/price-approvals").then((r) => setApprovals(r.data)).catch(() => setApprovals([]));
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditSku(null); setForm(EMPTY_PRODUCT); setDialogOpen(true); };
  const openEdit = (p) => {
    const f = { ...EMPTY_PRODUCT, ...p, price_change_reason: "" };
    for (const k of Object.keys(f)) if (typeof f[k] === "object" && f[k] !== null) continue; else if (f[k] == null) f[k] = "";
    for (const k of ["standard_rate", "msp", "floor_price", "max_discount_pct", "cost_price", "gst_percent", "pieces_per_carton", "min_stock", "max_stock"]) f[k] = p[k] ?? "";
    f.specs = { ...EMPTY_PRODUCT.specs, ...(p.specs || {}) };
    for (const k of Object.keys(f.specs)) if (f.specs[k] == null) f.specs[k] = "";
    f.packaging = { ...EMPTY_PRODUCT.packaging, ...(p.packaging || {}) };
    for (const k of Object.keys(f.packaging)) if (f.packaging[k] == null) f.packaging[k] = "";
    setEditSku(p.sku);
    setForm(f);
    setDialogOpen(true);
  };

  const saveProduct = async () => {
    if (!form.name || (!editSku && !form.sku)) return toast.error("SKU and name are required");
    try {
      if (editSku) {
        await api.put(`/products/${editSku}`, cleanProduct(form));
        toast.success(`${editSku} updated`);
      } else {
        await api.post("/products", cleanProduct(form));
        toast.success(`${form.sku.toUpperCase()} created`);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Save failed");
    }
  };

  const addColour = async () => {
    if (!colourForm.name.trim()) return toast.error("Colour name required");
    try {
      await api.post("/colours", colourForm);
      toast.success(`Colour ${colourForm.name} added`);
      setColourForm(EMPTY_COLOUR);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const addCustPrice = async () => {
    if (!cpForm.customer_code || !cpForm.sku || !cpForm.price) return toast.error("Customer, SKU and price are required");
    try {
      await api.post("/customer-prices", { ...cpForm, price: parseFloat(cpForm.price), effective_from: str(cpForm.effective_from), effective_to: str(cpForm.effective_to), remarks: str(cpForm.remarks) });
      toast.success("Customer price saved — it will auto-load on new orders");
      setCpOpen(false);
      setCpForm(EMPTY_CP);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const deleteCustPrice = async (cp) => {
    try {
      await api.delete(`/customer-prices/${cp.cp_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Delete failed");
    }
  };

  const decide = async (a, action) => {
    const remarks = action === "reject" ? window.prompt("Rejection reason (optional):") : null;
    try {
      await api.post(`/price-approvals/${a.approval_id}/decide`, { action, remarks });
      toast.success(`${a.sku} @ ₹${a.requested_price} ${action}d`);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const setSpec = (k, v) => setForm({ ...form, specs: { ...form.specs, [k]: v } });
  const setPkg = (k, v) => setForm({ ...form, packaging: { ...form.packaging, [k]: v } });
  const shown = useMemo(() => products.filter((p) => !search || `${p.sku} ${p.name} ${p.category} ${p.hsn}`.toLowerCase().includes(search.toLowerCase())), [products, search]);
  const pending = useMemo(() => approvals.filter((a) => a.status === "pending"), [approvals]);

  return (
    <div className="space-y-4" data-testid="products-page">
      <SectionTitle right={
        <div className="flex gap-2">
          <Input placeholder="Search SKU / name / HSN" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56 h-9" data-testid="product-search" />
          {canEdit && <Button onClick={openCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="create-product-button"><Plus className="w-4 h-4 mr-1" /> New Product</Button>}
        </div>
      }>Products & Pricing</SectionTitle>

      <div className="flex gap-1 border-b border-border" data-testid="products-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border-b-2 transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label}{k === "approvals" && pending.length > 0 ? ` (${pending.length})` : ""}
          </button>
        ))}
      </div>

      {tab === "products" && <ProductTable rows={shown} canEdit={canEdit} onEdit={openEdit} />}

      {tab === "colours" && (
        <div className="space-y-4">
          {canEdit && (
            <div className="bg-card border border-border rounded-lg p-4 grid md:grid-cols-6 gap-2 items-end" data-testid="colour-add-form">
              <F label="Colour Name *"><Input data-testid="colour-name-input" value={colourForm.name} onChange={(e) => setColourForm({ ...colourForm, name: e.target.value })} placeholder="Amber" /></F>
              <F label="Code"><Input data-testid="colour-code-input" value={colourForm.code} onChange={(e) => setColourForm({ ...colourForm, code: e.target.value })} placeholder="#FFBF00" /></F>
              <F label="Masterbatch Ref"><Input data-testid="colour-mb-input" value={colourForm.masterbatch_ref} onChange={(e) => setColourForm({ ...colourForm, masterbatch_ref: e.target.value })} /></F>
              <F label="Customer Ref"><Input data-testid="colour-cust-input" value={colourForm.customer_ref} onChange={(e) => setColourForm({ ...colourForm, customer_ref: e.target.value })} /></F>
              <F label="Remarks"><Input data-testid="colour-remarks-input" value={colourForm.remarks} onChange={(e) => setColourForm({ ...colourForm, remarks: e.target.value })} /></F>
              <Button onClick={addColour} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="colour-add-button"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            </div>
          )}
          <ColourTable rows={colours} />
        </div>
      )}

      {tab === "pricing" && (
        <div className="space-y-4">
          {canEdit && <Button onClick={() => setCpOpen(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="add-customer-price-button"><Plus className="w-4 h-4 mr-1" /> Add Customer Price</Button>}
          <CustomerPriceTable rows={custPrices} customers={customers} canEdit={canEdit} onDelete={deleteCustPrice} />
        </div>
      )}

      {tab === "approvals" && (
        <div className="bg-card border border-border rounded-lg overflow-x-auto" data-testid="price-approvals-table">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left">
              {["ID", "Order", "Customer", "SKU", "Std Price", "MSP", "Floor", "Cost", "Requested", "Margin", "Flags", "Salesperson", "Status", isAdmin ? "Decision" : ""].filter(Boolean).map((h) => (
                <th key={h} className="px-3 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {approvals.map((a) => (
                <tr key={a.approval_id} className="border-b border-border/50" data-testid={`approval-row-${a.approval_id}`}>
                  <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{a.approval_id}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-primary">{a.bill_no}</td>
                  <td className="px-3 py-2.5 text-xs">{a.customer}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{a.sku}</td>
                  <td className="px-3 py-2.5 metric text-xs">{money(a.standard_price)}</td>
                  <td className="px-3 py-2.5 metric text-xs">{money(a.msp)}</td>
                  <td className="px-3 py-2.5 metric text-xs">{money(a.floor_price)}</td>
                  <td className="px-3 py-2.5 metric text-xs text-muted-foreground">{money(a.cost)}</td>
                  <td className="px-3 py-2.5 metric text-xs font-bold text-signal-amber">{money(a.requested_price)}</td>
                  <td className="px-3 py-2.5 metric text-xs">{a.margin_pct != null ? `${a.margin_pct}%` : "—"}</td>
                  <td className="px-3 py-2.5">{(a.flags || []).map((f) => <span key={f} className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded mr-1 ${FLAG_CLS[f]}`}>{FLAG_LABEL[f]}</span>)}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{a.salesperson || "—"}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${a.status === "approved" ? "bg-signal-green/15 text-signal-green" : a.status === "pending" ? "bg-signal-amber/15 text-signal-amber" : "bg-signal-red/15 text-signal-red"}`} data-testid={`approval-status-${a.approval_id}`}>{a.status}</span>
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      {a.status === "pending" && (
                        <div className="flex gap-1">
                          <Button size="sm" className="h-7 bg-signal-green/20 text-signal-green hover:bg-signal-green/30" onClick={() => decide(a, "approve")} data-testid={`approval-approve-${a.approval_id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                          <Button size="sm" className="h-7 bg-signal-red/20 text-signal-red hover:bg-signal-red/30" onClick={() => decide(a, "reject")} data-testid={`approval-reject-${a.approval_id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {approvals.length === 0 && <tr><td colSpan={14} className="px-4 py-8 text-center text-sm text-muted-foreground">No price approval requests.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">{editSku ? `Edit ${editSku}` : "New Product / SKU"}</DialogTitle>
            <DialogDescription className="sr-only">Product master with specifications, packaging and pricing controls</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="space-y-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border pb-1">Basic Information</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <F label="SKU Code *"><Input data-testid="p-sku" value={form.sku} disabled={!!editSku} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} placeholder="PET-100-AVON-20-12-CLEAR" className="font-mono" /></F>
                <F label="Product Name *"><Input data-testid="p-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="100ml Round Avon PET Bottle" /></F>
                <F label="Category"><Input data-testid="p-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="PET Bottle" /></F>
                <F label="Subcategory"><Input data-testid="p-subcategory" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} /></F>
                <F label="Product Type">
                  <Select value={form.product_type || "none"} onValueChange={(v) => setForm({ ...form, product_type: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="p-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Select —</SelectItem>
                      {["Raw Material", "Packaging Material", "Work In Progress", "Finished Goods", "Consumables", "Spare Parts", "Scrap"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Brand"><Input data-testid="p-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></F>
                <F label="Customer Ref"><Input data-testid="p-custref" value={form.customer_ref} onChange={(e) => setForm({ ...form, customer_ref: e.target.value })} /></F>
                <F label="Status">
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="p-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="HSN Code"><Input data-testid="p-hsn" value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} /></F>
                <F label="GST %"><Input data-testid="p-gst" type="number" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} /></F>
                <F label="Barcode"><Input data-testid="p-barcode" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} /></F>
                <F label="UOM"><Input data-testid="p-uom" value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} /></F>
                <F label="Reorder Level"><Input data-testid="p-reorder" type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} /></F>
                <F label="Standard Location"><Input data-testid="p-stdloc" value={form.standard_location} onChange={(e) => setForm({ ...form, standard_location: e.target.value })} placeholder="Bin code" className="font-mono" /></F>
                <F label="Weight (g)"><Input data-testid="p-weight-g" type="number" step="0.01" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} /></F>
                <F label="Dimensions"><Input data-testid="p-dims" value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} placeholder="L×W×H mm" /></F>
                <div className="col-span-2">
                  <Label>Process Route (factory stages this SKU passes through — default: Production → QC → Packing)</Label>
                  <div className="flex gap-2 flex-wrap mt-1" data-testid="p-route-editor">
                    {["production", "processing", "frosting", "decoration", "qc", "packing"].map((st) => {
                      const on = (form.process_route || []).includes(st);
                      return (
                        <button type="button" key={st} data-testid={`p-route-${st}`}
                          onClick={() => setForm({ ...form, process_route: on ? (form.process_route || []).filter((x) => x !== st) : [...(form.process_route || []), st] })}
                          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border transition-colors ${on ? "bg-primary/15 border-primary text-primary" : "border-border text-muted-foreground hover:border-muted-foreground"}`}>
                          {st}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border pb-1">Technical Specifications</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <F label="Capacity"><Input data-testid="p-capacity" value={form.specs.capacity} onChange={(e) => setSpec("capacity", e.target.value)} placeholder="100" /></F>
                <F label="Unit"><Input data-testid="p-capunit" value={form.specs.capacity_unit} onChange={(e) => setSpec("capacity_unit", e.target.value)} placeholder="ml" /></F>
                <F label="Material"><Input data-testid="p-material" value={form.specs.material} onChange={(e) => setSpec("material", e.target.value)} placeholder="PET" /></F>
                <F label="Bottle Weight (g)"><Input data-testid="p-weight" type="number" step="0.01" value={form.specs.bottle_weight_g} onChange={(e) => setSpec("bottle_weight_g", e.target.value)} placeholder="12" /></F>
                <F label="Neck Size (mm)"><Input data-testid="p-neck" type="number" step="0.1" value={form.specs.neck_size_mm} onChange={(e) => setSpec("neck_size_mm", e.target.value)} placeholder="20" /></F>
                <F label="Neck Type / Finish"><Input data-testid="p-necktype" value={form.specs.neck_type} onChange={(e) => setSpec("neck_type", e.target.value)} /></F>
                <F label="Shape"><Input data-testid="p-shape" value={form.specs.shape} onChange={(e) => setSpec("shape", e.target.value)} placeholder="Round Avon" /></F>
                <F label="Colour">
                  <Select value={form.specs.colour || "none"} onValueChange={(v) => setSpec("colour", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="p-colour"><SelectValue placeholder="Select colour" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {colours.map((c) => <SelectItem key={c.colour_id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Diameter (mm)"><Input data-testid="p-diameter" type="number" step="0.1" value={form.specs.diameter_mm} onChange={(e) => setSpec("diameter_mm", e.target.value)} /></F>
                <F label="Height (mm)"><Input data-testid="p-height" type="number" step="0.1" value={form.specs.height_mm} onChange={(e) => setSpec("height_mm", e.target.value)} /></F>
                <F label="Custom Specs"><Input data-testid="p-customspecs" value={form.specs.custom_specs} onChange={(e) => setSpec("custom_specs", e.target.value)} /></F>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border pb-1">Packaging Configuration</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <F label="Pieces Per Carton"><Input data-testid="p-ppc" type="number" value={form.pieces_per_carton} onChange={(e) => setForm({ ...form, pieces_per_carton: e.target.value })} placeholder="500" /></F>
                <F label="Carton Type"><Input data-testid="p-cartontype" value={form.packaging.carton_type} onChange={(e) => setPkg("carton_type", e.target.value)} /></F>
                <F label="Carton Code"><Input data-testid="p-cartoncode" value={form.packaging.carton_code} onChange={(e) => setPkg("carton_code", e.target.value)} /></F>
                <F label="Carton Weight (g)"><Input data-testid="p-cartonweight" type="number" step="0.1" value={form.packaging.carton_weight_g} onChange={(e) => setPkg("carton_weight_g", e.target.value)} /></F>
                <F label="Carton L (mm)"><Input data-testid="p-cartonl" type="number" value={form.packaging.carton_length_mm} onChange={(e) => setPkg("carton_length_mm", e.target.value)} /></F>
                <F label="Carton W (mm)"><Input data-testid="p-cartonw" type="number" value={form.packaging.carton_width_mm} onChange={(e) => setPkg("carton_width_mm", e.target.value)} /></F>
                <F label="Carton H (mm)"><Input data-testid="p-cartonh" type="number" value={form.packaging.carton_height_mm} onChange={(e) => setPkg("carton_height_mm", e.target.value)} /></F>
                <F label="Packing Type"><Input data-testid="p-packingtype" value={form.packing_type} onChange={(e) => setForm({ ...form, packing_type: e.target.value })} /></F>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border pb-1">Pricing & Cost Controls</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <F label="Standard Selling Price ₹"><Input data-testid="p-rate" type="number" step="0.01" value={form.standard_rate} onChange={(e) => setForm({ ...form, standard_rate: e.target.value })} placeholder="6.00" /></F>
                <F label="Minimum Selling Price ₹"><Input data-testid="p-msp" type="number" step="0.01" value={form.msp} onChange={(e) => setForm({ ...form, msp: e.target.value })} placeholder="5.00" /></F>
                <F label="Absolute Floor Price ₹"><Input data-testid="p-floor" type="number" step="0.01" value={form.floor_price} onChange={(e) => setForm({ ...form, floor_price: e.target.value })} placeholder="4.20" /></F>
                <F label="Cost Price ₹"><Input data-testid="p-cost" type="number" step="0.01" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} placeholder="4.00" /></F>
                <F label="Max Discount %"><Input data-testid="p-maxdisc" type="number" step="0.1" value={form.max_discount_pct} onChange={(e) => setForm({ ...form, max_discount_pct: e.target.value })} /></F>
                <F label="Min Stock"><Input data-testid="p-minstock" type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} /></F>
                <F label="Max Stock"><Input data-testid="p-maxstock" type="number" value={form.max_stock} onChange={(e) => setForm({ ...form, max_stock: e.target.value })} /></F>
                {editSku && <F label="Price Change Reason"><Input data-testid="p-pricereason" value={form.price_change_reason} onChange={(e) => setForm({ ...form, price_change_reason: e.target.value })} placeholder="recorded in price history" /></F>}
              </div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Orders below MSP / floor / cost are auto-held for Admin/MD approval. HSN &amp; GST changes are restricted to manager/admin roles.</p>
            </div>

            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-11" onClick={saveProduct} data-testid="product-save-button">
              {editSku ? "Save Changes" : "Create Product"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cpOpen} onOpenChange={setCpOpen}>
        <DialogContent className="bg-card border-border" data-testid="customer-price-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Customer-Specific Price</DialogTitle>
            <DialogDescription className="sr-only">Contract price for a customer and SKU with effective dates</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <F label="Customer *">
              <Select value={cpForm.customer_code || "none"} onValueChange={(v) => setCpForm({ ...cpForm, customer_code: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="cp-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Select —</SelectItem>
                  {customers.map((c) => <SelectItem key={c.code} value={c.code}>{c.name} ({c.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="SKU *">
              <Select value={cpForm.sku || "none"} onValueChange={(v) => setCpForm({ ...cpForm, sku: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="cp-sku"><SelectValue placeholder="Select SKU" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Select —</SelectItem>
                  {products.map((p) => <SelectItem key={p.sku} value={p.sku}>{p.sku} — {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <div className="grid grid-cols-3 gap-3">
              <F label="Price ₹ *"><Input data-testid="cp-price" type="number" step="0.01" value={cpForm.price} onChange={(e) => setCpForm({ ...cpForm, price: e.target.value })} /></F>
              <F label="Effective From"><Input data-testid="cp-from" type="date" value={cpForm.effective_from} onChange={(e) => setCpForm({ ...cpForm, effective_from: e.target.value })} /></F>
              <F label="Effective To"><Input data-testid="cp-to" type="date" value={cpForm.effective_to} onChange={(e) => setCpForm({ ...cpForm, effective_to: e.target.value })} /></F>
            </div>
            <F label="Remarks"><Input data-testid="cp-remarks" value={cpForm.remarks} onChange={(e) => setCpForm({ ...cpForm, remarks: e.target.value })} /></F>
            <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" onClick={addCustPrice} data-testid="cp-save-button">Save Customer Price</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
